/**
 * Their numbers in our referral program (synced every night from the
 * platform): share links, people referred, commission earned / waiting /
 * owed / paid, and whether we can actually pay them.
 */
import { CircleCheck, CircleX, Coins, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Section, dayLabel, num, timeAgo, usd } from "@/features/command/ui";
import { CopyButton, shareLink } from "./shared";
import { orderedCodes } from "./PeopleList";
import type { Affiliate } from "./types";

function daysAgo(iso: string): string {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400_000);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: "warn" | "good" | "muted" }) {
  return (
    <div className="rounded-xl bg-zinc-900/50 px-3 py-2.5 ring-1 ring-inset ring-zinc-800/60">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={cn(
        "mt-0.5 text-lg font-semibold tabular-nums",
        tone === "warn" ? "text-amber-300" : tone === "good" ? "text-emerald-300" : tone === "muted" ? "text-zinc-400" : "text-zinc-50",
      )}>{value}</div>
    </div>
  );
}

function Ready({ ok, yes, no }: { ok: boolean; yes: string; no: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-[13px]", ok ? "text-emerald-300" : "text-zinc-400")}>
      {ok ? <CircleCheck className="h-4 w-4" /> : <CircleX className="h-4 w-4 text-rose-400" />}
      {ok ? yes : no}
    </span>
  );
}

export function ProgramCard({ a }: { a: Affiliate }) {
  const p = a.program ?? {};
  const inProgram = Boolean(p.synced_at || a.platform_user_id);
  const codes = orderedCodes(a);
  const rate = a.rate_pct != null && a.rate_pct !== "" ? Number(a.rate_pct) : null;

  return (
    <Section
      title="Their numbers"
      icon={Coins}
      action={p.synced_at ? <span className="text-[11px] text-zinc-500">Updated {timeAgo(p.synced_at)}</span> : undefined}
    >
      {!inProgram ? (
        <div className="space-y-3">
          <p className="text-sm text-zinc-400">
            {a.channel === "clickbank"
              ? "They sell through ClickBank, so their sales are counted there, not in our program. Keep notes on what they sold below."
              : a.channel === "other"
                ? "They send people to us from somewhere else, so there are no program numbers here. Keep notes on what they sent below."
                : "They aren't in our referral program yet. Once they join and get a code, their link and numbers show up here the next morning."}
          </p>
          {codes.length > 0 && <CodeList codes={codes} />}
        </div>
      ) : (
        <div className="space-y-4">
          {codes.length > 0 ? (
            <CodeList codes={codes} />
          ) : (
            <p className="text-sm text-zinc-500">No code yet. They can make one in their SyncSuite account under Referrals.</p>
          )}

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Figure label="Referred" value={`${num(p.referred ?? 0)} ${Number(p.referred ?? 0) === 1 ? "person" : "people"}`} />
            <Figure label="Earned" value={usd(p.gross_usd ?? 0)} />
            <Figure label="Still clearing" value={usd(p.pending_usd ?? 0)} tone="muted" />
            <Figure label="Owed now" value={usd(p.due_usd ?? 0)} tone={Number(p.due_usd ?? 0) > 0 ? "warn" : undefined} />
            <Figure label="Paid out" value={usd(p.paid_usd ?? 0)} tone={Number(p.paid_usd ?? 0) > 0 ? "good" : undefined} />
            <Figure label="Their cut" value={rate != null ? `${rate % 1 === 0 ? rate.toFixed(0) : rate}%` : "Not set"} />
          </div>

          <div className="flex flex-col gap-2 rounded-xl border border-zinc-800/60 px-3 py-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-5">
            <Ready ok={Boolean(p.payout_ready)} yes="Payout method set up" no="No payout method yet" />
            <Ready ok={Boolean(p.tax_ready)} yes="Tax form done" no="No tax form yet" />
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
            <div>
              <dt className="text-[11px] text-zinc-500">Last referral</dt>
              <dd className="text-zinc-200">
                {p.last_referral_at ? (
                  <>
                    {dayLabel(p.last_referral_at, { month: "short", day: "numeric", year: "numeric" })}
                    <span className="text-zinc-500"> · {daysAgo(p.last_referral_at)}</span>
                  </>
                ) : "None yet"}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] text-zinc-500">Joined the program</dt>
              <dd className="text-zinc-200">{a.joined_at ? dayLabel(a.joined_at, { month: "short", day: "numeric", year: "numeric" }) : "Not known"}</dd>
            </div>
          </dl>
        </div>
      )}
    </Section>
  );
}

function CodeList({ codes }: { codes: Array<{ code: string; off: boolean }> }) {
  return (
    <div className="space-y-1.5">
      {codes.map(({ code, off }) => (
        <div
          key={code}
          className={cn(
            "flex min-w-0 items-center gap-2 rounded-xl border px-3 py-2",
            off ? "border-zinc-800/50 bg-transparent" : "border-indigo-400/25 bg-indigo-500/[0.06]",
          )}
        >
          <Link2 className={cn("h-4 w-4 shrink-0", off ? "text-zinc-600" : "text-indigo-300")} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={cn("font-mono text-[13px] font-semibold", off ? "text-zinc-500" : "text-zinc-100")}>{code}</span>
              {off && <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-500">Switched off</span>}
            </div>
            <div className={cn("truncate text-[11px]", off ? "text-zinc-600" : "text-zinc-400")}>{shareLink(code)}</div>
          </div>
          {!off && <CopyButton text={shareLink(code)} label="Copy link" />}
        </div>
      ))}
    </div>
  );
}
